import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { Locale, Workspace } from '../api/types';
import { toLocale } from '../i18n';
import { DEFAULT_TIME_ZONE } from '../lib/format';

export interface WorkspaceContextValue {
  id: string;
  workspace: Workspace;
  timeZone: string;
  isAdmin: boolean;
  isOwner: boolean;
}

export const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

/** The workspace of the current route (inside /w/:workspaceId). */
export function useWs(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWs outside a workspace route');
  return ctx;
}

export function useOptionalWs(): WorkspaceContextValue | null {
  return useContext(WorkspaceContext);
}

/** Current UI locale, re-rendering on change. */
export function useLocale(): Locale {
  const { i18n } = useTranslation();
  return toLocale(i18n.resolvedLanguage ?? i18n.language);
}

/** Time zone for dates: the workspace's, else the default (NFR-I18N-02). */
export function useTimeZone(): string {
  return useContext(WorkspaceContext)?.timeZone ?? DEFAULT_TIME_ZONE;
}
